import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useOrgId } from './org';

export type ItemKind = 'service' | 'non_inventory' | 'inventory';

export interface Item {
  id: string;
  kind: ItemKind;
  name: string;
  sku: string | null;
  isSold: boolean;
  salesDescription: string | null;
  /** Decimal string in `currency`, or null when there is no default price. */
  salesPrice: string | null;
  salesAccountId: string | null;
  salesAccountName: string | null;
  salesTaxCodeId: string | null;
  salesTaxCode: string | null;
  isPurchased: boolean;
  purchaseDescription: string | null;
  purchasePrice: string | null;
  purchaseAccountId: string | null;
  purchaseAccountName: string | null;
  purchaseTaxCodeId: string | null;
  purchaseTaxCode: string | null;
  preferredVendorId: string | null;
  preferredVendorName: string | null;
  /** India: HSN (goods) or SAC (services) code and unit quantity code. */
  hsnSac?: string | null;
  uqc?: string | null;
  currency: string;
  isActive: boolean;
  createdAt: string;
  /** Inventory items (ADR-0015): stock account, quantity on hand, FIFO value, reorder point. */
  inventoryAssetAccountId?: string | null;
  inventoryAssetAccountName?: string | null;
  quantityOnHand?: string | null;
  inventoryValue?: string | null;
  reorderPoint?: string | null;
}

/** On hand at or below the reorder point (QBO's low stock). */
export const isLowStock = (i: Pick<Item, 'kind' | 'quantityOnHand' | 'reorderPoint'>): boolean =>
  i.kind === 'inventory' &&
  i.reorderPoint != null &&
  Number(i.quantityOnHand ?? 0) <= Number(i.reorderPoint);

export type ItemStatus = 'active' | 'inactive' | 'all';

export const ITEM_KIND_LABEL: Record<ItemKind, string> = {
  service: 'Service',
  non_inventory: 'Non-inventory',
  inventory: 'Inventory',
};

export const ITEMS_PATH = '/sales/products-and-services';

/** Products and services of the open client (filtering by type and search is client-side). */
export function useItems(opts: { status?: ItemStatus } = {}) {
  const orgId = useOrgId();
  const status = opts.status ?? 'active';
  return useQuery({
    queryKey: ['items', orgId, status],
    queryFn: () => api<Item[]>(`/orgs/${orgId}/items?status=${status}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}
